'use client';
import { Box } from '@mui/material';
import { type StructuredContentDocument } from '@repo/supergraph/structuredContent/types';
import React from 'react';
import { StructuredContentRenderer } from '../..';
import type { ColorToken } from '../../types';
import { ALIGN_Y_MAP, COLORS_MAP, SPACING_MAP } from './constants';
import type { SpacingObject, SpacingToken } from './types';
import { isSpacingObject } from './utils';

type ColumnsProps = StructuredContentDocument<{
  alignY?: 'top' | 'center' | 'bottom';
  backgroundColor?: ColorToken;
  children: StructuredContentDocument;
  collapse?: boolean;
  padding?: SpacingToken;
  spacing?: SpacingObject | SpacingToken;
}>;

interface ColumnsContextValue {
  collapse: boolean;
}

export const ColumnsContext = React.createContext<ColumnsContextValue>({
  collapse: false,
});

export const Columns = ({ props }: ColumnsProps) => {
  const {
    alignY,
    backgroundColor,
    children,
    collapse = false,
    padding,
    spacing = { mobile: 3, desktop: '3-half' },
  } = props;

  const gapDesktop = SPACING_MAP[isSpacingObject(spacing) ? spacing.desktop : spacing] ?? 3.5;
  const gapMobile = SPACING_MAP[isSpacingObject(spacing) ? spacing.mobile : spacing] ?? 3;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: collapse ? 'minmax(0, 1fr)' : 'repeat(12, minmax(0, 1fr))',
          lg: 'repeat(12, minmax(0, 1fr))',
        },
        alignItems: alignY ? ALIGN_Y_MAP[alignY] : undefined,
        p: SPACING_MAP[padding || 0],
        gap: { xs: gapMobile, lg: gapDesktop },
        bgcolor: COLORS_MAP[(backgroundColor || 'transparent') as ColorToken],
      }}
    >
      <ColumnsContext.Provider value={{ collapse }}>
        <StructuredContentRenderer doc={children} />
      </ColumnsContext.Provider>
    </Box>
  );
};
